/**
 * ReportScenarios — Compact scenario cards with expandable detail.
 *
 * Surface: scenario title, submission count, top variables (bar chart), related pivot points
 * Expand: all variable details with AI summaries, participant quotes
 */
import { useState, useMemo } from 'react';
import styled from '@emotion/styled';
import { ChevronDown, ChevronUp, Network } from 'lucide-react';
import ScenarioIcon from '../ScenarioIcon';
import WorkshopAction from './WorkshopAction';
import { SCENARIO_COLORS_BY_NAME } from '../../config/brandColors';
import EditableText from './EditableText';
import { useReportEdits } from './ReportEditsContext';
import Markdown from './Markdown';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import ReportImageSlot from './ReportImageSlot';
import {
  Section, SectionTitle, Paragraph,
  BarChart, BarRow, BarLabel, BarTrack, BarFill,
} from './reportStyles';

// ─── Styled Components ───────────────────────────────────────────────────────

const ScenarioGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 20px;
`;

const ScenarioCard = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-left: 4px solid ${p => p.$color || '#6b7280'};
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  @media print { page-break-inside: avoid; box-shadow: none; }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  cursor: pointer;
  user-select: none;
  &:hover { background: #fafafa; }
  @media print { &:hover { background: transparent; } }
`;


const HeaderInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ScenarioTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #000;
  margin: 0;
  line-height: 1.3;
`;

const ScenarioMeta = styled.div`
  font-size: 13px;
  color: #6b7280;
  margin-top: 2px;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const CountBadge = styled.span`
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 0;
  background: #f5f5f5;
  color: #367ABA;
`;

const ToggleIcon = styled.span`
  color: #9ca3af;
  @media print { display: none; }
`;

const OneLiner = styled.p`
  font-size: 15px;
  font-weight: 600;
  line-height: 1.5;
  color: #000;
  margin: 0;
  padding: 0 20px 6px;
`;

// Curator-supplied "what does this tulevaisuuskuva mean in 2-3 sentences"
// blurb, rendered right under the scenario header when the overlay key
// scenario.<id>.tulevaisuuskuva_description is set. Sits between the title
// and the AI-generated kärkilause/summary, italic-styled so the reader
// can tell it's a curator framing rather than AI prose.
const TulevaisuuskuvaDesc = styled.p`
  font-size: 13.5px;
  line-height: 1.55;
  color: #374151;
  font-style: italic;
  margin: 0;
  padding: 0 20px 10px;
  border-left: 3px solid rgba(0, 0, 0, 0.08);
  margin-left: 20px;
  padding-left: 12px;
`;

const AiSummary = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: #4b5563;
  margin: 0;
  padding: 0 20px 12px;
`;

const ReadMoreBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 4px 20px 14px;
  padding: 0;
  background: none;
  border: none;
  color: #367ABA;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  &:hover { color: #2c5e91; text-decoration: underline; }
  @media print { display: none; }
`;

// Expanded detail
const DetailBody = styled.div`
  padding: 0 20px 20px;
  border-top: 1px solid #f3f4f6;
`;

const DetailSubtitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: #475569;
  margin: 16px 0 8px;
`;

const VariableCard = styled.div`
  background: ${p => p.$highlight ? '#fafafa' : '#fff'};
  border: 1px solid ${p => p.$highlight ? '#e2e8f0' : '#f0f0f0'};
  border-left: 3px solid ${p => p.$color || '#6b7280'};
  border-radius: 0;
  padding: 12px 16px;
  margin-bottom: 8px;
`;

const VarTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #000;
  margin-bottom: 4px;
`;

const VarDescription = styled.div`
  font-size: 13px;
  color: #4b5563;
  line-height: 1.5;
  margin-bottom: 4px;
`;

const AiTag = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #7c3aed;
  background: #ede9fe;
  padding: 1px 6px;
  border-radius: 4px;
  margin-right: 6px;
`;

const VarSummary = styled.div`
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
  font-style: italic;
`;

const QuoteBlock = styled.blockquote`
  background: #f5f5f5;
  border-left: 4px solid ${p => p.$color || '#367ABA'};
  padding: 12px 16px;
  margin: 8px 0;
  border-radius: 0;
  font-style: italic;
  color: #2c5e91;
  font-size: 14px;
  line-height: 1.6;
  cursor: ${p => p.$clickable ? 'pointer' : 'default'};
`;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getScenarioKey(title) {
  const lower = title?.toLowerCase() || '';
  for (const key of ['ilmasto', 'demokratia', 'väestö', 'vaesto', 'teknologia']) {
    if (lower.includes(key)) return key;
  }
  return null;
}

function getColor(title) {
  const lower = title?.toLowerCase() || '';
  for (const [key, color] of Object.entries(SCENARIO_COLORS_BY_NAME)) {
    if (lower.includes(key.toLowerCase())) return color;
  }
  return '#6b7280';
}

const MAX_QUOTE = 200;

// ─── Component ───────────────────────────────────────────────────────────────

export default function ReportScenarios({ scenarios, pathways }) {
  const { t } = useTranslation();
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [expandedQuotes, setExpandedQuotes] = useState(new Set());
  // Used to suppress the curator description slot when neither a saved
  // value nor an admin in edit mode is around — keeps the layout tight
  // on instances that don't have descriptions written yet.
  const { get, getTranslated, canEdit, editMode } = useReportEdits();

  // Parent lookup for tree walking
  const parentLookup = useMemo(() => {
    const lookup = {};
    pathways.forEach(p => { lookup[p.id] = p; });
    return lookup;
  }, [pathways]);

  // Submissions per scenario
  const submissionsByScenario = useMemo(() => {
    const grouped = {};
    scenarios.forEach(s => {
      grouped[s.id] = pathways.filter(p =>
        (p.scenarioId || p.scenario_id) === s.id &&
        p.type !== 'scenario_root' && p.type !== 'variable_root' && !p.is_scenario_starter
      );
    });
    return grouped;
  }, [pathways, scenarios]);

  // Variable data per scenario
  const variableDataByScenario = useMemo(() => {
    const result = {};
    scenarios.forEach(scenario => {
      const varCounts = {};
      pathways
        .filter(p => (p.scenarioId || p.scenario_id) === scenario.id && p.type === 'human')
        .forEach(sub => {
          let current = sub;
          while (current?.parentId) {
            const parent = parentLookup[current.parentId];
            if (!parent) break;
            if (parent.type === 'scenario_variable') {
              varCounts[parent.text || 'Unknown'] = (varCounts[parent.text || 'Unknown'] || 0) + 1;
              break;
            }
            if (parent.type === 'scenario_root') break;
            current = parent;
          }
        });

      const selectedTk = scenario.selected_tulevaisuuskuva;
      const relevantVars = selectedTk
        ? (scenario.variables || []).filter(v => v.tulevaisuuskuva === selectedTk)
        : (scenario.variables || []);
      const varLookup = {};
      relevantVars.forEach(v => {
        varLookup[v.title] = { description: v.description || null, summary: v.consequence_summary || null };
      });

      result[scenario.id] = Object.entries(varCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 6)
        .map(([name, count]) => ({
          name, count,
          description: varLookup[name]?.description || null,
          summary: varLookup[name]?.summary || null,
        }));
    });
    return result;
  }, [scenarios, pathways, parentLookup]);

  // Quotes per scenario
  const quotesByScenario = useMemo(() => {
    const result = {};
    scenarios.forEach(scenario => {
      result[scenario.id] = (submissionsByScenario[scenario.id] || [])
        .filter(s => (s.text || s.content || s.description || '').length > 60)
        .sort((a, b) => (b.text || b.content || '').length - (a.text || a.content || '').length)
        .slice(0, 3)
        .map((s) => ({ id: s.id, text: s.text || s.content || s.description }));
    });
    return result;
  }, [scenarios, submissionsByScenario]);

  if (!scenarios?.length) return null;

  const toggle = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <Section id="scenarios">
      <SectionTitle>{t('report.impactMap.title')}</SectionTitle>
      <WorkshopAction>
        <EditableText
          fieldKey="phase.scenarios.intro"
          fallback={t('report.impactMap.description')}
        />
      </WorkshopAction>

      {/* Käsikirjoitus 2026-05: upotettu vaikutuskartta tai screenshot vipupisteineen */}
      <ReportImageSlot
        fieldKeyPrefix="scenarios.map"
        label={t('report.scenarios.mapLabel')}
        aspect="16 / 9"
        hint={t('report.scenarios.mapHint')}
      />

      <ScenarioGrid>
        {scenarios.map(scenario => {
          const color = getColor(scenario.title);
          const scenarioKey = getScenarioKey(scenario.title);
          const varData = variableDataByScenario[scenario.id] || [];
          const quotes = quotesByScenario[scenario.id] || [];
          const maxCount = varData[0]?.count || 1;
          const submissionCount = (submissionsByScenario[scenario.id] || []).length;
          const isExpanded = expandedIds.has(scenario.id);

          return (
            <ScenarioCard key={scenario.id} $color={color}>
              <CardHeader style={{ cursor: 'default' }}>
                {scenarioKey && <ScenarioIcon scenario={scenarioKey} color={color === '#FFD93D' ? '#C49B00' : color} size={32} strokeWidth={2} />}
                <HeaderInfo>
                  <ScenarioTitle>
                    {scenario.id
                      ? <EditableText fieldKey={`scenario.${scenario.id}.title`} fallback={scenario.title} multiline={false} label="Skenaarion otsikko" />
                      : scenario.title}
                    {scenario.selected_tulevaisuuskuva && (
                      <>: &ldquo;<EditableText fieldKey={`scenario.${scenario.id}.selected_tulevaisuuskuva`} fallback={scenario.selected_tulevaisuuskuva} multiline={false} label="Valittu tulevaisuuskuva" />&rdquo;</>
                    )}
                  </ScenarioTitle>
                  <ScenarioMeta>
                    {t('report.impactMap.ideasVars', { ideas: submissionCount, vars: varData.length })}
                  </ScenarioMeta>
                </HeaderInfo>
                <CountBadge>{submissionCount}</CountBadge>
              </CardHeader>

              {/* Curator-provided tulevaisuuskuva description (overlay) —
                  renders right under the title if set. Empty fallback keeps
                  the slot hidden on instances where no description has been
                  written yet (admins in edit mode still see the slot so
                  they can add one). */}
              {scenario.id && (() => {
                const tkKey = `scenario.${scenario.id}.tulevaisuuskuva_description`;
                const tkValue = (get(tkKey, '') || '').trim();
                if (!tkValue && !(canEdit && editMode)) return null;
                return (
                  <TulevaisuuskuvaDesc>
                    <EditableText
                      fieldKey={tkKey}
                      fallback=""
                      label={`${scenario.title} — tulevaisuuskuvan kuvaus`}
                    />
                  </TulevaisuuskuvaDesc>
                );
              })()}

              {/* One-liner (kärkilause, kept plain text) + summary
                  (rendered as markdown so **bold**, *italic*, headings,
                  lists render properly inside the card). Scenario
                  description is AI-generated markdown — leading "# H1"
                  banner is stripped by <Markdown> since the card title
                  above already plays that role. */}
              {scenario.description && (() => {
                // For the kärkilause we want only the first prose sentence,
                // so strip markdown markers and take everything up to .!?.
                // In a translated view, derive from the translated description
                // so oneLiner/summary don't need their own translation rows.
                const stripMarkdown = s => (s || '')
                  .replace(/^#\s+[^\n]*\n+/, '')
                  .replace(/^#+\s+/gm, '')
                  .trim();
                const desc = getTranslated(
                  `scenario.${scenario.id}.description`,
                  scenario.description,
                );
                const stripped = stripMarkdown(desc);
                const firstSentence = stripped.match(/^[^.!?]+[.!?]/)?.[0] || '';
                // The summary keeps the raw markdown (leading H1 + the rest)
                // so <Markdown> can render bold/italic/list structure faithfully.
                const restMarkdown = desc
                  .replace(/^#\s+[^\n]*\n+/, '')   // drop the leading H1 (redundant)
                  .trim();
                return (
                  <>
                    {firstSentence && (
                      <OneLiner>
                        {scenario.id
                          ? <EditableText
                              fieldKey={`scenario.${scenario.id}.oneLiner`}
                              fallback={firstSentence}
                              transform={stripMarkdown}
                              label={`${scenario.title} — kärkilause`}
                            />
                          : firstSentence}
                      </OneLiner>
                    )}
                    {restMarkdown && (
                      <AiSummary>
                        {scenario.id
                          ? <EditableText
                              fieldKey={`scenario.${scenario.id}.summary`}
                              fallback={restMarkdown}
                              markdown
                              block
                              label={`${scenario.title} — yhteenveto`}
                            />
                          : <Markdown>{restMarkdown}</Markdown>}
                      </AiSummary>
                    )}
                  </>
                );
              })()}

              {/* Read more toggle */}
              {(varData.length > 0 || quotes.length > 0) && (
                <ReadMoreBtn onClick={() => toggle(scenario.id)}>
                  {isExpanded ? <><ChevronUp size={14} /> {t('report.impactMap.hide')}</> : <><ChevronDown size={14} /> {t('report.impactMap.readMore', { vars: varData.length, ideas: submissionCount })}</>}
                </ReadMoreBtn>
              )}

              {/* Expanded: bar charts + variable details + quotes */}
              {isExpanded && (
                <DetailBody>
                  {varData.length > 0 && (
                    <>
                      <DetailSubtitle>Muuttujat ({varData.length})</DetailSubtitle>
                      <BarChart>
                        {varData.map(({ name, count }) => (
                          <BarRow key={name}>
                            <BarLabel>{name}</BarLabel>
                            <BarTrack>
                              <BarFill $width={(count / maxCount) * 100} $color={color}>
                                {count}
                              </BarFill>
                            </BarTrack>
                          </BarRow>
                        ))}
                      </BarChart>
                    </>
                  )}

                  {varData.some(v => v.description || v.summary) && (
                    <>
                      <DetailSubtitle>Muuttujien AI-koosteet</DetailSubtitle>
                      {varData.map((v, i) => (
                        (v.description || v.summary) && (
                          <VariableCard key={v.name} $color={color} $highlight={i === 0}>
                            <VarTitle>{i === 0 && '🔥 '}{v.name} ({v.count})</VarTitle>
                            {v.description && (
                              <VarDescription>
                                <EditableText
                                  fieldKey={`scenario.${scenario.id}.variable.${v.name}.description`}
                                  fallback={v.description}
                                  label={`${v.name} — kuvaus`}
                                />
                              </VarDescription>
                            )}
                            {v.summary && (
                              <VarSummary>
                                <AiTag>AI</AiTag>
                                <EditableText
                                  fieldKey={`scenario.${scenario.id}.variable.${v.name}.summary`}
                                  fallback={v.summary}
                                  label={`${v.name} — yhteenveto`}
                                />
                              </VarSummary>
                            )}
                          </VariableCard>
                        )
                      ))}
                    </>
                  )}

                  {quotes.length > 0 && (
                    <>
                      <DetailSubtitle>Osallistujien seurauksia</DetailSubtitle>
                      {quotes.map(({ id, text }) => {
                        const isLong = text.length > MAX_QUOTE;
                        const isOpen = expandedQuotes.has(id);
                        const display = isLong && !isOpen ? text.slice(0, MAX_QUOTE) + '...' : text;
                        return (
                          <QuoteBlock
                            key={id}
                            $color={color}
                            $clickable={isLong}
                            onClick={isLong ? () => setExpandedQuotes(prev => {
                              const next = new Set(prev);
                              if (next.has(id)) next.delete(id); else next.add(id);
                              return next;
                            }) : undefined}
                          >
                            &ldquo;<EditableText fieldKey={`quote.${id}.text`} fallback={display} label="Osallistujan seuraus" />&rdquo;
                          </QuoteBlock>
                        );
                      })}
                    </>
                  )}
                </DetailBody>
              )}
            </ScenarioCard>
          );
        })}
      </ScenarioGrid>
    </Section>
  );
}
